import { useState, useRef } from "react";

const inputSt = {
  width: "100%", background: "#111", border: "1px solid #242424", borderRadius: 8,
  color: "#e8e0d0", fontSize: 14, fontFamily: "inherit", padding: "12px 14px",
  boxSizing: "border-box", outline: "none",
};

function FieldChip({ label, value, C }) {
  if (!value) return null;
  return (
    <div style={{ background: C.bg, borderRadius: 6, padding: "4px 8px", fontSize: 11, color: C.muted }}>
      {label && <span style={{ color: C.dim, marginRight: 4 }}>{label}</span>}
      {value}
    </div>
  );
}

function ReadLabel({ children, C }) {
  return (
    <div style={{ fontSize: 9, color: C.dim, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>
      {children}
    </div>
  );
}

function ReadValue({ children, C }) {
  return (
    <div style={{ fontSize: 14, color: C.text, marginBottom: 16 }}>
      {children || <span style={{ color: C.dim, fontStyle: "italic" }}>—</span>}
    </div>
  );
}

export default function InstrumentViewPage({ instr, C, accentRgb, onBack, onOpenGallery, onSaved }) {
  const orange = C?.orange || "#E8621A";
  const urls = (() => { try { const r = JSON.parse(instr.image_urls || "[]"); return Array.isArray(r) ? r : []; } catch(e) { return []; } })();
  const heroUrl = instr.hero_image_url || urls[0];
  const condClr = { Excellent: "#34c759", Good: "#7ecb8f", Fair: "#f59e0b", Poor: "#ef4444" }[instr.condition];

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  // Reminders — stored as JSONB on the catalogue record
  // TODO: wire push notification delivery when backend notification service is added
  const parseReminders = (raw) => {
    try { const r = JSON.parse(raw || "[]"); return Array.isArray(r) ? r : []; } catch(e) { return []; }
  };
  const [reminders, setReminders] = useState(() => parseReminders(instr.reminders));
  const [addingReminder, setAddingReminder] = useState(false);
  const [reminderSaving, setReminderSaving] = useState(false);
  const [newReminder, setNewReminder] = useState({ title: "", date: "", recurrence: "none" });
  const idCounter = useRef(Date.now());

  const saveReminders = async (updated) => {
    setReminders(updated);
    try {
      await fetch(`/api/catalogue/${instr.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reminders: updated }),
      });
    } catch(e) { console.error("Reminder save error:", e); }
  };

  const addReminder = async () => {
    if (!newReminder.title.trim() || !newReminder.date) return;
    setReminderSaving(true);
    const entry = { id: ++idCounter.current, ...newReminder, title: newReminder.title.trim() };
    await saveReminders([...reminders, entry]);
    setNewReminder({ title: "", date: "", recurrence: "none" });
    setAddingReminder(false);
    setReminderSaving(false);
  };

  const deleteReminder = async (id) => {
    await saveReminders(reminders.filter(r => r.id !== id));
  };

  const recurrenceLabel = { none: "Once", weekly: "Weekly", monthly: "Monthly", annually: "Annually" };
  const recurrenceClr = { none: "#585450", weekly: "#6aaee0", monthly: "#E8621A", annually: "#5ab870" };
  const [form, setForm] = useState({
    model: instr.model || "",
    brand: instr.brand || "",
    serial: instr.serial || "",
    year: instr.year || "",
    origin: instr.origin || "",
    instrument: instr.instrument || "",
    strings: instr.strings || "",
    string_gauge: instr.string_gauge || "",
    bridge: instr.bridge || "",
    condition: instr.condition || "",
    purchase_price: instr.purchase_price || "",
    purchase_date: instr.purchase_date || "",
    notes: instr.notes || "",
  });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.model.trim()) { setSaveError("Make / Model is required."); return; }
    setSaveError("");
    setSaving(true);
    try {
      const resp = await fetch(`/api/catalogue/${instr.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (resp.ok) {
        onSaved(instr.id, form);
        setEditing(false);
      } else {
        setSaveError("Save failed — please try again.");
      }
    } catch(e) {
      setSaveError("Save failed — please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ maxWidth: 900, margin: "0 auto", padding: "20px 20px 120px" }}>

      {/* Back */}
      <button onClick={onBack} style={{ background: "none", border: "none", color: C.dim, fontFamily: "inherit", fontSize: 13, cursor: "pointer", padding: "0 0 16px", display: "flex", alignItems: "center", gap: 6 }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
        Instruments
      </button>

      {/* Hero photo */}
      {heroUrl ? (
        <div onClick={() => onOpenGallery && onOpenGallery(instr)} style={{ marginBottom: 20, cursor: urls.length > 0 ? "pointer" : "default", position: "relative" }}>
          <img src={heroUrl} alt="" style={{ width: "100%", height: 220, objectFit: "cover", borderRadius: 14, border: `1px solid ${C.border}`, display: "block" }} />
          {urls.length > 1 && (
            <div style={{ position: "absolute", bottom: 10, right: 10, background: "rgba(0,0,0,0.65)", borderRadius: 20, padding: "4px 10px", fontSize: 11, color: "#fff", fontWeight: 600 }}>
              {urls.length} photos
            </div>
          )}
        </div>
      ) : (
        <div style={{ width: "100%", height: 160, borderRadius: 14, background: C.surface, border: `1.5px dashed ${C.border}`, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 20 }}>
          <svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke={C.dim} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>
        </div>
      )}

      {/* Title */}
      <div style={{ fontSize: 24, fontWeight: 700, color: C.text, letterSpacing: "-0.02em", marginBottom: 4 }}>{instr.model || "Unknown"}</div>
      <div style={{ fontSize: 14, color: C.muted, marginBottom: 20 }}>{[instr.brand, instr.instrument, instr.year].filter(Boolean).join(" · ")}</div>

      {!editing ? (
        /* ── READ-ONLY VIEW ── */
        <>
          {/* Identity chips */}
          {(instr.serial || instr.year || instr.origin || instr.strings || instr.bridge || instr.string_gauge) && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 20 }}>
              <FieldChip label="S/N" value={instr.serial} C={C} />
              <FieldChip value={instr.year} C={C} />
              <FieldChip value={instr.origin} C={C} />
              <FieldChip value={instr.strings} C={C} />
              <FieldChip label="Bridge" value={instr.bridge} C={C} />
              <FieldChip value={instr.string_gauge} C={C} />
            </div>
          )}

          {/* Condition */}
          {instr.condition && (
            <div style={{ marginBottom: 20 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: condClr, background: `${condClr}18`, padding: "4px 12px", borderRadius: 20, border: `1px solid ${condClr}30` }}>{instr.condition}</span>
            </div>
          )}

          {/* Purchase details */}
          {(instr.purchase_price || instr.purchase_date) && (
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: "14px 16px", marginBottom: 16 }}>
              <div style={{ fontSize: 10, color: C.dim, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}>Purchase Details</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                {instr.purchase_price && (
                  <div>
                    <ReadLabel C={C}>Price Paid</ReadLabel>
                    <div style={{ fontSize: 18, fontWeight: 700, color: orange }}>${parseFloat(instr.purchase_price).toFixed(0)}</div>
                  </div>
                )}
                {instr.purchase_date && (
                  <div>
                    <ReadLabel C={C}>Date</ReadLabel>
                    <div style={{ fontSize: 14, color: C.muted }}>{instr.purchase_date}</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Notes */}
          {instr.notes && (
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: "14px 16px", marginBottom: 16 }}>
              <div style={{ fontSize: 10, color: C.dim, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>Notes</div>
              <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{instr.notes}</div>
            </div>
          )}

          {/* ── Reminders ── */}
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: "14px 16px", marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: reminders.length > 0 || addingReminder ? 12 : 0 }}>
              <div style={{ fontSize: 10, color: C.dim, textTransform: "uppercase", letterSpacing: "0.08em" }}>Reminders</div>
              {!addingReminder && (
                <button onClick={() => setAddingReminder(true)} style={{ display: "flex", alignItems: "center", gap: 5, background: "none", border: `1px solid ${C.border}`, borderRadius: 8, padding: "4px 10px", color: C.muted, fontFamily: "inherit", fontSize: 12, cursor: "pointer" }}>
                  <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  Add
                </button>
              )}
            </div>

            {/* Existing reminders */}
            {reminders.map(r => (
              <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 0", borderBottom: `1px solid ${C.border}` }}>
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke={C.dim} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.title}</div>
                  <div style={{ fontSize: 11, color: C.dim, marginTop: 2 }}>{r.date}</div>
                </div>
                <span style={{ fontSize: 10, fontWeight: 600, color: recurrenceClr[r.recurrence] || C.dim, background: `${recurrenceClr[r.recurrence] || C.dim}18`, padding: "2px 8px", borderRadius: 10, flexShrink: 0 }}>
                  {recurrenceLabel[r.recurrence] || r.recurrence}
                </span>
                <button onClick={() => deleteReminder(r.id)} style={{ background: "none", border: "none", color: C.dim, cursor: "pointer", padding: 4, lineHeight: 0, flexShrink: 0 }}>
                  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>
            ))}

            {reminders.length === 0 && !addingReminder && (
              <div style={{ fontSize: 12, color: C.dim, fontStyle: "italic" }}>No reminders yet.</div>
            )}

            {/* Add reminder form */}
            {addingReminder && (
              <div style={{ marginTop: reminders.length > 0 ? 12 : 0 }}>
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 9, color: C.dim, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>Title</div>
                  <input
                    value={newReminder.title}
                    onChange={e => setNewReminder(p => ({ ...p, title: e.target.value }))}
                    placeholder="e.g. String change due"
                    style={inputSt}
                    autoFocus
                  />
                </div>
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 9, color: C.dim, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>Date</div>
                  <input
                    type="date"
                    value={newReminder.date}
                    onChange={e => setNewReminder(p => ({ ...p, date: e.target.value }))}
                    style={inputSt}
                  />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 9, color: C.dim, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>Repeat</div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {["none","weekly","monthly","annually"].map(rec => (
                      <button key={rec} onClick={() => setNewReminder(p => ({ ...p, recurrence: rec }))} style={{
                        padding: "6px 12px", borderRadius: 8, fontFamily: "inherit", fontSize: 12, cursor: "pointer",
                        border: `1px solid ${newReminder.recurrence === rec ? recurrenceClr[rec] : C.border}`,
                        background: newReminder.recurrence === rec ? `${recurrenceClr[rec]}18` : "transparent",
                        color: newReminder.recurrence === rec ? recurrenceClr[rec] : C.muted,
                      }}>{recurrenceLabel[rec]}</button>
                    ))}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={addReminder} disabled={!newReminder.title.trim() || !newReminder.date || reminderSaving} style={{ flex: 1, padding: "10px", borderRadius: 8, border: "none", background: newReminder.title.trim() && newReminder.date ? (C.goldDim || "#C24E10") : "#222", color: newReminder.title.trim() && newReminder.date ? "#fff" : C.dim, fontFamily: "inherit", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
                    {reminderSaving ? "Saving…" : "Save Reminder"}
                  </button>
                  <button onClick={() => { setAddingReminder(false); setNewReminder({ title: "", date: "", recurrence: "none" }); }} style={{ padding: "10px 14px", borderRadius: 8, border: `1px solid ${C.border}`, background: "transparent", color: C.muted, fontFamily: "inherit", fontSize: 13, cursor: "pointer" }}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Edit button */}
          <button
            onClick={() => setEditing(true)}
            style={{ width: "100%", padding: "13px", borderRadius: 10, border: `1px solid ${C.border}`, background: "transparent", color: C.muted, fontFamily: "inherit", fontSize: 14, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
          >
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
            Edit Instrument
          </button>
        </>
      ) : (
        /* ── EDIT MODE ── */
        <div style={{ pointerEvents: saving ? "none" : "auto", opacity: saving ? 0.6 : 1 }}>
          <div style={{ fontSize: 11, color: orange, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 16 }}>Editing</div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
            <div style={{ gridColumn: "1/-1" }}>
              <ReadLabel C={C}>Make / Model *</ReadLabel>
              <input value={form.model} onChange={e => set("model", e.target.value)} placeholder="e.g. Fender Jazz Bass" style={inputSt} />
            </div>
            <div>
              <ReadLabel C={C}>Brand</ReadLabel>
              <input value={form.brand} onChange={e => set("brand", e.target.value)} placeholder="e.g. Fender" style={inputSt} />
            </div>
            <div>
              <ReadLabel C={C}>Serial Number</ReadLabel>
              <input value={form.serial} onChange={e => set("serial", e.target.value)} placeholder="e.g. Z1234567" style={inputSt} />
            </div>
            <div>
              <ReadLabel C={C}>Year</ReadLabel>
              <input value={form.year} onChange={e => set("year", e.target.value)} placeholder="e.g. 2019" style={inputSt} />
            </div>
            <div>
              <ReadLabel C={C}>Origin</ReadLabel>
              <input value={form.origin} onChange={e => set("origin", e.target.value)} placeholder="e.g. Japan" style={inputSt} />
            </div>
            <div>
              <ReadLabel C={C}>Strings</ReadLabel>
              <input value={form.strings} onChange={e => set("strings", e.target.value)} placeholder="e.g. 4-String" style={inputSt} />
            </div>
            <div>
              <ReadLabel C={C}>String Gauge</ReadLabel>
              <input value={form.string_gauge} onChange={e => set("string_gauge", e.target.value)} placeholder="e.g. .045–.105" style={inputSt} />
            </div>
            <div>
              <ReadLabel C={C}>Bridge</ReadLabel>
              <input value={form.bridge} onChange={e => set("bridge", e.target.value)} placeholder="e.g. Mono-rail" style={inputSt} />
            </div>
            <div>
              <ReadLabel C={C}>Purchase Price ($)</ReadLabel>
              <input value={form.purchase_price} onChange={e => set("purchase_price", e.target.value)} type="number" placeholder="e.g. 850" style={inputSt} />
            </div>
            <div>
              <ReadLabel C={C}>Purchase Date</ReadLabel>
              <input value={form.purchase_date} onChange={e => set("purchase_date", e.target.value)} type="date" style={inputSt} />
            </div>
            <div style={{ gridColumn: "1/-1" }}>
              <ReadLabel C={C}>Notes</ReadLabel>
              <textarea value={form.notes} onChange={e => set("notes", e.target.value)} rows={3} placeholder="Mods, condition, history…" style={{ ...inputSt, resize: "vertical" }} />
            </div>
          </div>

          {saveError && (
            <div style={{ marginBottom: 12, padding: "10px 14px", background: "#1a0c0c", border: "1px solid #3d1e1e", borderRadius: 8, fontSize: 13, color: "#e06060" }}>{saveError}</div>
          )}

          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={handleSave} disabled={!form.model.trim() || saving} style={{ flex: 1, padding: "13px", borderRadius: 10, border: "none", background: form.model.trim() ? (C.goldDim || "#C24E10") : "#222", color: form.model.trim() ? "#fff" : C.dim, fontFamily: "inherit", fontWeight: 600, fontSize: 14, cursor: form.model.trim() ? "pointer" : "not-allowed" }}>
              {saving ? "Saving…" : "Save Changes"}
            </button>
            <button onClick={() => { setEditing(false); setSaveError(""); }} style={{ padding: "13px 18px", borderRadius: 10, border: `1px solid ${C.border}`, background: "transparent", color: C.muted, fontFamily: "inherit", fontSize: 14, cursor: "pointer" }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
